// src/pages/HomePage.js
import React, { useEffect, useRef } from 'react';
import ProductCarousel from '../components/ProductCarousel';
import StorePage       from './StorePage';
import AcademiaPage    from './AcademiaPage';

const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const bs = { fontFamily: "'Montserrat', sans-serif" };

const SectionLabel = ({ children }) => (
  <p style={{ ...bs, color: '#961914', fontSize: '10px', letterSpacing: '0.38em',
    textTransform: 'uppercase', marginBottom: '12px', fontWeight: 600 }}>
    {children}
  </p>
);

const SectionTitle = ({ children, light }) => (
  <h2 style={{ ...ds, fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 400,
    color: light ? 'white' : '#060D14', lineHeight: 1.15, marginBottom: '20px' }}>
    {children}
  </h2>
);

const Divider = ({ light }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px' }}>
    <div style={{ height: '1px', width: '40px', background: light ? 'rgba(255,255,255,0.3)' : '#e5c9c9' }} />
    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: light ? 'rgba(255,255,255,0.4)' : '#961914' }} />
    <div style={{ height: '1px', width: '40px', background: light ? 'rgba(255,255,255,0.3)' : '#e5c9c9' }} />
  </div>
);

const HomePage = ({ onProductClick }) => {
  const heroRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (heroRef.current) {
        heroRef.current.style.backgroundPositionY = `${window.scrollY * 0.35}px`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section ref={heroRef} style={{
        minHeight: '93vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(150deg, #961914 0%, #6b0f0b 45%, #060D14 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Textura puntos */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.07,
          backgroundImage: 'radial-gradient(white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        {/* Línea vertical decorativa */}
        <div style={{
          position: 'absolute', left: '6vw', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
        }} className="hidden lg:flex">
          <div style={{ width: '1px', height: '80px', background: 'rgba(255,255,255,0.2)' }} />
          <span style={{ ...bs, color: 'rgba(255,255,255,0.3)', fontSize: '9px',
            letterSpacing: '0.3em', textTransform: 'uppercase', writingMode: 'vertical-rl' }}>
            Tlalpan · CDMX
          </span>
          <div style={{ width: '1px', height: '80px', background: 'rgba(255,255,255,0.2)' }} />
        </div>

        <div style={{ textAlign: 'center', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <p style={{ ...bs, color: 'rgba(255,255,255,0.5)', fontSize: '10px',
            letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: '20px' }}>
            Academia &amp; Boutique de Danza
          </p>

          <h1 style={{
            ...ds, color: 'white', fontWeight: 300, lineHeight: 1,
            fontSize: 'clamp(4rem, 12vw, 9rem)', letterSpacing: '0.06em',
            marginBottom: '28px',
          }}>
            ELEDÉ
          </h1>

          {/* Ornamento */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '14px', marginBottom: '28px' }}>
            <div style={{ height: '1px', width: '50px', background: 'rgba(255,255,255,0.25)' }} />
            <svg viewBox="0 0 20 20" width="14" height="14"
              style={{ color: 'rgba(255,255,255,0.3)', fill: 'currentColor' }}>
              <path d="M10 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z"/>
            </svg>
            <div style={{ height: '1px', width: '50px', background: 'rgba(255,255,255,0.25)' }} />
          </div>

          <p style={{ ...ds, color: 'rgba(255,255,255,0.75)', fontStyle: 'italic',
            fontWeight: 300, fontSize: 'clamp(1rem, 2.2vw, 1.3rem)',
            maxWidth: '540px', margin: '0 auto 44px', lineHeight: 1.7 }}>
            "No somos una academia más, somos Eledé: danza con arte, disciplina y distinción."
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <button onClick={() => document.getElementById('store-section')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ ...bs, padding: '14px 36px', background: 'white', color: '#961914',
                fontWeight: 600, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)', transition: 'all 0.25s' }}
              onMouseOver={e => { e.currentTarget.style.background = '#f5f0f0'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Ver Tienda
            </button>
            <button onClick={() => document.getElementById('academia-section')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ ...bs, padding: '14px 36px', background: 'transparent', color: 'white',
                fontWeight: 500, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
                border: '1px solid rgba(255,255,255,0.45)', cursor: 'pointer', transition: 'all 0.25s' }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}>
              Academia
            </button>
          </div>
        </div>

        {/* Scroll hint */}
        <div style={{ position: 'absolute', bottom: '32px', left: '50%',
          transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: '8px', animation: 'bounce 2s infinite' }}>
          <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.3)' }} />
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.35)">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
          </svg>
        </div>

        <style>{`@keyframes bounce { 0%,100%{transform:translateX(-50%) translateY(0)} 50%{transform:translateX(-50%) translateY(6px)} }`}</style>
      </section>

      {/* ── POR QUÉ ELEGIRNOS ────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(60px,8vw,100px) 24px', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <SectionLabel>Nuestra diferencia</SectionLabel>
            <SectionTitle>¿Por qué elegirnos?</SectionTitle>
            <Divider />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '48px' }}>
            {[
              { n: '01', t: 'Trayectoria profesional única',
                d: 'Fundada y dirigida por ex-solistas del Ballet de Monterrey. Experiencia artística internacional y excelencia técnica.' },
              { n: '02', t: 'Boutique de danza especializada',
                d: 'Productos de alta calidad elegidos por bailarines profesionales. Servicio personalizado para encontrar el calzado y vestuario perfecto.' },
              { n: '03', t: 'Clases selectas y personalizadas',
                d: 'Grupos reducidos, calidad antes que cantidad, desde Baby Ballet hasta nivel profesional, en un recinto exclusivo en Tlalpan Centro.' },
            ].map(({ n, t, d }) => (
              <div key={n} style={{ position: 'relative', paddingTop: '16px' }}>
                <div style={{ ...ds, position: 'absolute', top: '-24px', left: '-8px',
                  fontSize: '72px', fontWeight: 300, color: '#f8f0f0',
                  lineHeight: 1, userSelect: 'none' }}>{n}</div>
                <div style={{ position: 'relative', borderTop: '2px solid #f3e8e8',
                  paddingTop: '20px' }}>
                  <h3 style={{ ...ds, fontSize: '1.3rem', fontWeight: 600,
                    color: '#060D14', marginBottom: '12px', lineHeight: 1.3 }}>{t}</h3>
                  <p style={{ ...bs, color: '#888', fontSize: '13px',
                    lineHeight: 1.75 }}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACADEMIA ─────────────────────────────────────────────────────── */}
      <div style={{ background: '#faf9f9' }} id="academia-section">
        <AcademiaPage />
      </div>

      {/* ── PRODUCTOS DESTACADOS ─────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(60px,8vw,96px) 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <SectionLabel>Selección especial</SectionLabel>
            <SectionTitle>Productos Destacados</SectionTitle>
            <Divider />
          </div>
          <ProductCarousel onProductClick={onProductClick} />
        </div>
      </section>

      {/* ── TIENDA COMPLETA ──────────────────────────────────────────────── */}
      <StorePage onProductClick={onProductClick} />

    </div>
  );
};

export default HomePage;